import { PropsWithChildren, ReactElement } from "react";
import style from "./StatCard.module.css";
import Skeleton from "../Skeleton/Skeleton";
import { FormatValueOptions, formatValue } from "@/utils/formatUtils";
import { Tooltip } from "../Tooltip/Tooltip";
import IconInfo from "../icons/IconInfo";

interface StatCardProps {
  className?: string;
  onClick?: () => void;
}

function StatCard({ children, className, onClick }: PropsWithChildren<StatCardProps>) {
  return (<div className={`${style.statCard} ${!!className ? className : ""}`} onClick={onClick}>{children}</div>)
}

interface StatCardHeaderProps {
  label: string;
  icon?: ReactElement;
  className?: string;
  info?: string | ReactElement;
}

function StatCardHeader({ icon, label, children, className, info }: PropsWithChildren<StatCardHeaderProps>) {
  return (<div className={`${style.statCardHeader} ${!!className ? className : ""}`}>
    {icon && <div>{icon}</div>}
    <h3><span>{label}</span>{info && <Tooltip
      title={typeof info === "string" ? info : ""}
    >
      <IconInfo />
    </Tooltip>}</h3>
    {children}
  </div>)
}

interface StatCardContentProps {
  className?: string;
}

function StatCardContent({ children, className }: PropsWithChildren<StatCardContentProps>) {
  return (<div className={`${style.statCardContent} ${!!className ? className : ""}`}>{children}</div>)
}

interface StatCardDataProps {
  value: string | number;
  valueChangePercentage?: number;
  valueChangeDescription?: string;
  formatAs?: FormatValueOptions;
  currency?: string;
  isLoading?: boolean;
  className?: string;
}

function StatCardData({ value, valueChangePercentage, valueChangeDescription, formatAs, currency, isLoading, className }: StatCardDataProps) {
  return (<div className={`${style.statCardData} ${!!className ? className : ""}`}>
    <h4>{isLoading ? <Skeleton height={32} /> : formatValue(value, formatAs, currency)}</h4>
    {valueChangePercentage &&
      <div><span>{valueChangePercentage}</span>
        {valueChangeDescription && <span>{valueChangeDescription}</span>}
      </div>
    }
  </div>)
}

interface StatCardListProps {
  className?: string;
}
function StatCardList({ children, className }: PropsWithChildren<StatCardListProps>) {
  return (<div className={`${style.statCardList} ${!!className ? className : ""}`}>{children}</div>)
}

interface StatCardListItemProps {
  label: string;
  value: string | number;
  formatAs?: FormatValueOptions;
  currency?: string;
  isLoading?: boolean;
  className?: string;
}

function StatCardListItem({ label, value, formatAs, currency, isLoading, className }: StatCardListItemProps) {
  return (<div className={`${style.statCardListItem} ${!!className ? className : ""}`}>
    <div>{label}</div>
    {isLoading ? <Skeleton width={60} /> : <span>{formatValue(value, formatAs, currency)}</span>}
  </div>)
}

StatCard.Header = StatCardHeader;
StatCard.Content = StatCardContent;
StatCard.Data = StatCardData;
StatCard.List = StatCardList;
StatCard.ListItem = StatCardListItem;

export default StatCard;